import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Edit dialog for a single position's role training matrix. List inputs are
 * comma-separated. The backend accepts arrays — we split/join transparently.
 */
export default function RoleMatrixDialog({ open, onOpenChange, row, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    position: '',
    mandatory: '',
    optional: '',
    recerts: '',
    internal: '',
    external: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) {
      setForm({ position: '', mandatory: '', optional: '', recerts: '', internal: '', external: '' });
      return;
    }
    setForm({
      position: row.position || '',
      mandatory: (row.mandatory || []).join(', '),
      optional: (row.optional || []).join(', '),
      recerts:  (row.recerts || []).join(', '),
      internal: (row.internal || []).join(', '),
      external: (row.external || []).join(', '),
    });
  }, [row]);

  const split = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const handleSave = useCallback(async () => {
    if (!form.position.trim()) {
      toast.error(t('clientTalentTrain.matrix.dialog.positionRequired'));
      return;
    }
    setSaving(true);
    try {
      await clientApi.ttSetRoleMatrix(form.position.trim(), {
        position: form.position.trim(),
        mandatory: split(form.mandatory),
        optional:  split(form.optional),
        recerts:   split(form.recerts),
        internal:  split(form.internal),
        external:  split(form.external),
      });
      toast.success(t('clientTalentTrain.matrix.dialog.saved'));
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('clientTalentTrain.matrix.dialog.saveFailed'), { description: formatApiError(e) });
    } finally { setSaving(false); }
  }, [form, t, onSaved, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ctt-matrix-dialog">
        <DialogHeader>
          <DialogTitle>{t('clientTalentTrain.matrix.dialog.title')}</DialogTitle>
          <DialogDescription>{t('clientTalentTrain.matrix.dialog.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="rm-position">{t('clientTalentTrain.matrix.th.position')}</Label>
            <Input
              id="rm-position"
              value={form.position}
              disabled={!!row}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              data-testid="ctt-matrix-dialog-position"
            />
          </div>
          {[
            ['mandatory', t('clientTalentTrain.matrix.th.mandatory')],
            ['optional',  t('clientTalentTrain.matrix.th.optional')],
            ['recerts',   t('clientTalentTrain.matrix.th.recerts')],
            ['internal',  t('clientTalentTrain.matrix.dialog.internal')],
            ['external',  t('clientTalentTrain.matrix.dialog.external')],
          ].map(([k, lbl]) => (
            <div key={k} className="grid gap-1.5">
              <Label htmlFor={`rm-${k}`}>
                {lbl}
                <span className="text-xs text-muted-foreground ml-2">
                  {t('clientTalentTrain.matrix.dialog.commaSep')}
                </span>
              </Label>
              <Textarea
                id={`rm-${k}`}
                value={form[k]}
                rows={2}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`ctt-matrix-dialog-${k}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ctt-matrix-dialog-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
